import React, { useMemo, useState } from "react";
import { Icon, IconButton } from "@fluentui/react";
import { mergeStyleSets } from "@fluentui/react/lib/Styling";
import { useTheme } from "../../../../theme/OAS365Theme";
import { useGridStyle } from "../../../../theme/styles/GridStyle";
import Tooltip from "../../../../components/Tooltip/Tooltip";
import { getDocumentClassFormatLabel } from "../../../../functions/getDocumentClassFormatLabel";

const tableColumnClassNames = mergeStyleSets({
  fileIconImg: {
    verticalAlign: "middle",
    maxHeight: "16px",
    maxWidth: "16px",
  },
});

function getContextMenuTypography(theme) {
  return {
    fontFamily: theme.contextMenu?.fontFamily ?? theme.fonts.regular,
    fontSize: theme.contextMenu?.fontSize ?? theme.fontSizes?.menusize ?? 12,
  };
}

function getRowMenuProps(theme, actions) {
  if (!actions) return () => undefined;
  const menuType = getContextMenuTypography(theme);
  const {
    onDetails,
    onCheckout,
    getCheckoutButtonText,
    onCheckoutDocumentChange,
    onPreviewInTeams,
    onOpenIFS,
    onDownload,
  } = actions;

  return (item) => ({
    shouldFocusOnMount: true,
    items: [
      {
        key: "CheckOut",
        text: getCheckoutButtonText?.(item) ?? "Check Out",
        itemProps: {
          styles: {
            root: {
              color: item.isCheckoutDisable
                ? theme.colors.addovation30Gray
                : theme.colors.fontColor,
              ...menuType,
            },
          },
        },
        disabled: item.isCheckoutDisable,
        onClick: () => {
          onCheckout?.(item);
          onCheckoutDocumentChange?.(item);
        },
      },
      {
        key: "openteamsInifsAurena",
        text: "Open in IFS Cloud",
        itemProps: {
          styles: {
            root: {
              color: theme.colors.fontColor,
              ...menuType,
            },
          },
        },
        onClick: () => onOpenIFS?.(item),
      },
      {
        key: "Download",
        text: "Download",
        itemProps: {
          styles: {
            root: {
              color: item.isDownloadDisable
                ? theme.colors.addovation30Gray
                : theme.colors.fontColor,
              ...menuType,
            },
          },
        },
        disabled: item.isDownloadDisable,
        onClick: () => onDownload?.(item),
      },
      {
        key: "openteamsifs",
        text: "Preview in Teams",
        itemProps: {
          styles: {
            root: {
              color: item.isPreviewDisable
                ? theme.colors.addovation30Gray
                : theme.colors.fontColor,
              ...menuType,
            },
          },
        },
        disabled: item.isPreviewDisable,
        onClick: () => {
          onPreviewInTeams?.(item);
        },
      },
      {
        key: "details",
        text: "Details",
        itemProps: {
          styles: {
            root: {
              color: theme.colors.fontColor,
              ...menuType,
            },
          },
        },
        onClick: (ev) => {
          onDetails?.(item);
          ev?.stopPropagation?.();
        },
      },
    ],
    styles: {
      root: {
        border: `1px solid ${theme.colors.addovation20Green} !important`,
        ...menuType,
        selectors: {
          ".ms-ContextualMenu-link:hover": {
            backgroundColor: theme.colors.contextMenuHover,
            color: theme.colors.fontColor,
          },
          ".ms-ContextualMenu-linkContent": {
            ...menuType,
          },
          ".ms-ContextualMenu-itemText": {
            ...menuType,
          },
          ".ms-FocusZone, .css-134, .ms-ContextualMenu, .is-open, .ms-BaseButton-menuhost": {
            backgroundColor: theme.colors.contextMenuBackground,
          },
        },
      },
    },
  });
}

function formatCodeWithName(code, name) {
  const codeText = (code ?? "").trim();
  const nameText = (name ?? "").trim();
  if (codeText && nameText) return `${codeText} - ${nameText}`;
  return codeText || nameText;
}

/**
 * Builds tooltip content array for a document row: Title, Document No, Class, Format, State, Modified, Status.
 */
function getRowTooltipContent(item) {
  const classText = formatCodeWithName(item.class, item.className);
  const formatText = formatCodeWithName(item.format, item.formatName);
  return [
    `Title: ${item.title ?? ""}`,
    `Document No: ${item.docNo ?? ""} - ${item.docRev ?? ""}`,
    classText ? `Class: ${classText}` : "",
    item.format != null && item.format !== "" ? `Format: ${formatText || item.format}` : "",
    `State: ${item.state ?? ""}`,
    `Modified: ${item.lastActionDate ?? ""}`,
    item.status != null && item.status !== "" ? `Status: ${item.status}` : "",
  ].filter(Boolean);
}

/**
 * Builds the table column definitions (used internally by useDocumentTableColumns).
 */
function buildDocumentTableColumns({ theme, gridThemeStyle, actions}) {
  const classNames = tableColumnClassNames;
  const menuProps = getRowMenuProps(theme, actions);
  const onOpenIFS = actions?.onOpenIFS;
  return [
    {
      key: "rowOffsetSpacer",
      name: "",
      fieldName: "__rowOffsetSpacer",
      minWidth: 36,
      maxWidth: 36,
      isResizable: false,
      onRender: () => null,
    },
    {
      key: "fileType",
      ariaLabel: "Column operations for File type, Press to sort on File type",
      fieldName: "name",
      minWidth: 16,
      maxWidth: 16,
      styles: gridThemeStyle.headerCellStyle,
      onRender: (item) => (
        <Tooltip
          content={getRowTooltipContent(item)}
          listType="div"
          position="right"
          maxWidth={200}
          delay={100}
        >
          <img
            src={item.iconName}
            className={classNames.fileIconImg}
            style={{ width: 16, height: 16, objectFit: "contain" }}
            alt={`${item.fileType} file icon`}
          />
        </Tooltip>
      ),
    },
    {
      key: "docNo",
      name: "Document No",
      minWidth: 110,
      maxWidth: 110,
      color: "#f000",
      isResizable: true,
      onRender: (item) => (
        <span
          onClick={(ev) => {
            ev.preventDefault();
            ev.stopPropagation();
            onOpenIFS?.(item);
          }}
          style={{
            cursor: "pointer",
            textDecoration: "underline",
            color: theme.colors.themeDarkColor ?? "#249f5d",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "inline-block",
            maxWidth: "100%",
          }}
        >
          {`${item.docNo ?? ""} - ${item.docRev ?? ""}`}
        </span>
      ),
    },
    {
      key: "title",
      name: "Title",
      fieldName: "title",
      minWidth: 300,
      maxWidth: 300,
      isResizable: true,
      isSortedDescending: false,
      sortAscendingAriaLabel: "Sorted A to Z",
      sortDescendingAriaLabel: "Sorted Z to A",
      onRender: (item) => (
        <div
          style={{
            position: "relative",
            display: "block",
            width: "300px",
            maxWidth: "300px",
            boxSizing: "border-box",
            overflow: "hidden",
          }}
        >
          {item.isRecentCheckIn && (
            <div
              style={{
                position: "absolute",
                top: -11,
                left: -11,
                zIndex: 1,
              }}
            >
              <Icon
                iconName="Glimmer"
                style={{
                  color: theme.colors.addovation70Green,
                  padding: 4,
                  fontSize: 10,
                }}
              />
            </div>
          )}
          <span
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "block",
              width: "300px",
              minWidth: 0,
              maxWidth: "300px",
            }}
          >
            {item.title}
          </span>
        </div>
      ),
    },
    {
      key: "documentAndOptions",
      name: "",
      minWidth: 30,
      maxWidth: 30,
      color: theme.colors.fontColor,
      isResizable: false,
      onRender: (item) => (
        <div
          style={{
            display: "flex",
            width: "100%",
            height: "100%",
            maxWidth: "100%",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div>
            <IconButton
              menuProps={menuProps(item)}
              menuIconProps={{ iconName: "More" }}
              styles={{
                root: {
                  color: theme.colors.themeDarkColor,
                  fontFamily: `${theme.fonts.regular}`,
                  fontSize: theme.fontSizes.medium,
                  minHeight: 0,
                  height: "100%",
                  padding: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  selectors: {
                    ".ms-ContextualMenu-itemText": {
                      ...getContextMenuTypography(theme),
                      backgroundColor: theme.colors.msTeamsFilesHover,
                    },
                    ".ms-ContextualMenu-linkContent": {
                      ...getContextMenuTypography(theme),
                      backgroundColor: theme.colors.msTeamsFilesHover,
                    },
                  },
                },
              }}
            />
          </div>
        </div>
      ),
    },
    {
      key: "class",
      name: "Class/Format",
      minWidth: 160,
      maxWidth: 160,
      color: "#f000",
      isResizable: true,
      onRender: (item) => {
        const classFormatText = getDocumentClassFormatLabel(item.class, item.formatName);
        return (
          <div
            style={{
              position: "relative",
              display: "inline-block",
              width: "100%",
            }}
          >
            <span
              style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "inline-block",
                maxWidth: "100%",
              }}
            >
              {classFormatText}
            </span>
          </div>
        );
      },
    },
    {
      key: "lastActionDate",
      name: "Last Action",
      minWidth: 160,
      maxWidth: 400,
      //flexGrow: 1,
      color: "#f000",
      isResizable: true,
      fontFamily: "Poppins, sans-serif",
      onRender: (item) => (
        <div
          style={{
            whiteSpace: "nowrap",
            // overflow: "hidden",
            // textOverflow: "ellipsis",
            // width: "100%",
          }}
        >
          {item.lastActionDate}
        </div>
      ),
    },
  ];
}

/**
 * Hook that returns memoized table column definitions.
 * Menu items (Check-out, Open in IFS Cloud, Download, Preview in Teams, Details) are defined in this file;
 * parent passes only the action handlers.
 * @param {Object} actions - { onDetails, onCheckout, getCheckoutButtonText, onCheckoutDocumentChange, onPreviewInTeams, onOpenIFS, onDownload }
 */
const LARGE_SCREEN_MIN_WIDTH = 1400;

export function useDocumentTableColumns(actions) {
  const theme = useTheme();
  const gridThemeStyle = useGridStyle();

  return useMemo(
    () => buildDocumentTableColumns({ theme, gridThemeStyle, actions }),
    [theme, gridThemeStyle, actions, ]
  );
}
